import React, { useEffect } from 'react';
import { getConfiguredShareImage, getPublicSiteUrl } from '../../utils/publicSiteUrl';
import { HeadMeta, managedMetas, normalisePath, serverHeadFor } from '../../utils/serverHead';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string[];
  canonicalUrl?: string;
  noIndex?: boolean;
  exactTitle?: boolean;
  suppressDefaults?: boolean;
  jsonLd?: string[];
}

interface HeadState {
  title: string;
  canonical: string;
  metas: HeadMeta[];
  jsonLd: string[];
}

const SITE_NAME = 'Carmen Lorraine Photography';
const DEFAULT_TITLE = 'Carmen Lorraine Photography | Cape Town Photographer';
const DEFAULT_DESCRIPTION = 'Creative, natural photography in Cape Town by Carmen Lorraine. Specialising in portraits, lifestyle, commercial, business and property photography.';
const DESCRIPTION_LIMIT = 160;

let appliedSignature = '';

function readSavedSettings(pathname: string): Partial<SEOHeadProps> {
  try {
    const raw = localStorage.getItem('seo_settings');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const defaults = parsed?.defaults && typeof parsed.defaults === 'object' ? parsed.defaults : {};
    const perPage = parsed?.perPage && typeof parsed.perPage === 'object' ? parsed.perPage[pathname] || {} : {};
    return { ...defaults, ...perPage };
  } catch (e) {
    return {};
  }
}

function clampDescription(value: string): string {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= DESCRIPTION_LIMIT) return text;
  const cut = text.slice(0, DESCRIPTION_LIMIT - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > 100 ? cut.slice(0, space) : cut).replace(/[\s,;:.-]+$/, '')}…`;
}

function absoluteUrl(value: string, origin: string): string {
  const raw = String(value || '').trim();
  if (!raw || raw === 'undefined' || raw === 'null') return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

function canonicalFor(value: string | undefined, origin: string, pathname: string): string {
  const source = absoluteUrl(value || '', origin) || `${origin}${pathname}`;
  try {
    const url = new URL(source);
    return `${url.origin}${normalisePath(url.pathname)}`;
  } catch (e) {
    return `${origin}${normalisePath(pathname)}`;
  }
}

function clientHead(props: SEOHeadProps, pathname: string): HeadState {
  const merged: SEOHeadProps = { ...readSavedSettings(pathname) };
  (Object.keys(props) as Array<keyof SEOHeadProps>).forEach((key) => {
    if (props[key] !== undefined) (merged as any)[key] = props[key];
  });

  const origin = getPublicSiteUrl();
  const baseTitle = merged.title || (props.suppressDefaults ? '' : DEFAULT_TITLE);
  const title = props.exactTitle || baseTitle.includes(SITE_NAME) ? baseTitle : `${baseTitle} | ${SITE_NAME}`;
  const description = clampDescription(merged.description || (props.suppressDefaults ? '' : DEFAULT_DESCRIPTION));
  const canonical = canonicalFor(merged.canonicalUrl || merged.url, origin, pathname);
  const image = absoluteUrl(merged.image || '', origin) || absoluteUrl(getConfiguredShareImage(), origin);
  const imageAlt = merged.imageAlt || title;
  const type = merged.type || 'website';

  const metas: HeadMeta[] = [];
  const add = (attr: HeadMeta['attr'], key: string, content?: string) => {
    if (content) metas.push({ attr, key, content });
  };
  add('name', 'description', description);
  add('name', 'robots', merged.noIndex ? 'noindex, nofollow' : 'max-image-preview:large');
  add('property', 'og:type', type);
  add('property', 'og:title', title);
  add('property', 'og:description', description);
  add('property', 'og:url', canonical);
  add('property', 'og:site_name', SITE_NAME);
  if (image) {
    add('property', 'og:image', image);
    add('property', 'og:image:alt', imageAlt);
  }
  add('name', 'twitter:card', 'summary_large_image');
  add('name', 'twitter:title', title);
  add('name', 'twitter:description', description);
  if (image) {
    add('name', 'twitter:image', image);
    add('name', 'twitter:image:alt', imageAlt);
  }
  if (type === 'article') {
    add('property', 'article:published_time', merged.publishedTime);
    add('property', 'article:modified_time', merged.modifiedTime);
    add('property', 'article:author', merged.author);
    const tags = (merged.keywords || []).filter(Boolean);
    add('property', 'article:section', tags[0]);
    tags.slice(1).forEach((tag) => add('property', 'article:tag', tag));
  }

  const jsonLd = Array.isArray(merged.jsonLd) ? merged.jsonLd.map((item) => String(item || '')).filter(Boolean) : [];
  return { title, canonical, metas, jsonLd };
}

function applyHead(state: HeadState): void {
  document.title = state.title;

  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (state.canonical) {
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.setAttribute('href', state.canonical);
  } else if (link) {
    link.remove();
  }

  managedMetas().forEach((meta) => meta.remove());
  state.metas.forEach(({ attr, key, content }) => {
    const meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  });

  document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => script.remove());
  state.jsonLd.forEach((payload) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = payload;
    document.head.appendChild(script);
  });
}

/**
 * On the URL the server rendered, the server head is authoritative and is
 * left untouched. After in-app navigation to another URL, a clean head is
 * built for that URL; returning to the server-rendered URL restores it.
 */
const SEOHead: React.FC<SEOHeadProps> = (props) => {
  const signatureSource = JSON.stringify(props);

  useEffect(() => {
    const pathname = normalisePath(window.location.pathname);
    const server = serverHeadFor(pathname);
    if (server) {
      if (!appliedSignature) return;
      applyHead(server);
      appliedSignature = '';
      return;
    }
    const state = clientHead(JSON.parse(signatureSource) as SEOHeadProps, pathname);
    const signature = `${pathname}|${JSON.stringify(state)}`;
    if (signature === appliedSignature) return;
    applyHead(state);
    appliedSignature = signature;
  }, [signatureSource]);

  return null;
};

export function resetSEOHeadForTests(): void {
  appliedSignature = '';
}

export default SEOHead;
